<script lang="ts">
  import { onMount } from 'svelte';
  import { potter, pottePlanter, loadPottePlanter } from '../lib/stores';
  import { supabase } from '../lib/supabase';
  import { startPolling } from '../lib/polling';
  import { hentSensorHistorikk } from '../lib/sensorhistorikk';
  import {
    blomsterkasseOppsett,
    bakSeksjon,
    jordfuktProsent,
    jordGrenser,
    kasseForm,
    sensorEtikett,
    tilkoblingsStatus,
  } from '../lib/utils';
  import { kasseTanker, tankStatus } from '../lib/tanker';
  import { kasseNaering } from '../lib/naering';
  import { probeHelse, vekeHelse, luftFunn, overvaatHelse } from '../lib/diagnose';
  import { simStore, hentSim, simHistorikk, effektivKasse } from '../lib/simulering';
  import { visFeil } from '../lib/toast';
  import type { Potte, PotteCommand, PotteSensorData, PottePlanteFull, PotteRunde } from '../lib/database.types';
  import AnleggPanel from './AnleggPanel.svelte';
  import SensorGrafer from './SensorGrafer.svelte';
  import type { SerieId } from '../lib/grafer';
  import PlanteVelger from './PlanteVelger.svelte';
  import Veksttidslinje from './Veksttidslinje.svelte';
  import Sheet from './Sheet.svelte';
  import KasseInnstillinger from './KasseInnstillinger.svelte';
  import PlantingRedigering from './PlantingRedigering.svelte';
  import { lagrePlanting, byttPlante } from '../lib/plantinger';
  import { sensorOppsett, maskerAvlesning } from '../lib/sensorer';

  let { potteId }: { potteId: string } = $props();

  const potte = $derived($potter.find((p) => p.potte_id === potteId));
  const planter = $derived($pottePlanter[potteId] ?? []);
  // Oppsettet (potter + plasser) regnes ut fra skillevegg-lista — én sannhet.
  const oppsett = $derived(potte ? blomsterkasseOppsett(potte.skillevegger, kasseForm(potte)) : []);

  let command = $state<PotteCommand | null>(null);
  let lysKlar = $state(false);
  let sensor = $state<PotteSensorData | null>(null);
  let velgerSeksjon = $state<number | null>(null);
  let skilleveggFeil = $state<string | null>(null);
  let forsteHentet = $state(false); // før første svar vet vi ikke om potta har postet
  let nettFeil = $state(false); // siste henting feilet — tallene er de sist hentede
  let henter = false;
  let naa = $state(Date.now());
  let historikk = $state<PottePlanteFull[]>([]);
  let runder = $state<PotteRunde[]>([]);
  let runderKlar = $state(false);
  let runderFeil = $state(false);
  let visHistorikk = $state(false);
  let valgtRunde = $state<PotteRunde | null>(null);
  const aktivRunde = $derived(runder.find((r) => r.avsluttet_at === null));
  const tidligereRunder = $derived(runder.filter((r) => r.avsluttet_at !== null));
  const vistHistorikk = $derived(historikk.filter((h) => valgtRunde ? h.runde_id === valgtRunde.id : !h.runde_id));
  let redigerHist = $state<PottePlanteFull | null>(null);
  let visInnstillinger = $state(false);
  // Grafarket: null = lukket, ellers hvilken serie som er valgt ved åpning.
  let graf = $state<{ forvalgt: SerieId | null } | null>(null);

  // Sensorhistorikk (siste 7 dager) → vanntrend + jordfukt-sparkline per felt.
  type SensorRad = {
    registrert_at: string | null;
    vann_avstand_mm: number | null;
    vann2_avstand_mm?: number | null;
    jord1: number | null;
    jord2: number | null;
    jord3: number | null;
    jord4: number | null;
  };
  let sensorHistorikk = $state<SensorRad[]>([]);

  // ---- Testmodus-simulator: syntetiske data + gate-flip så ALT forhåndsvises ----
  // effektivKasse() er DEN ENE delte implementasjonen (også brukt av
  // PotteOversikt) — se simulering.ts for hvorfor duplisering her var risikabelt.
  const effektiv = $derived(potte ? effektivKasse(potte, planter, sensor ?? undefined, $simStore) : null);
  const simAktiv = $derived(effektiv?.simAktiv ?? false);
  const effektivPotte = $derived(effektiv?.potte ?? potte);
  // Sensorer kassa ikke har, nulles her. Da viser ingenting under dem, og
  // varsler og diagnoser ser dem ikke (lib/sensorer.ts).
  const sensOppsett = $derived(sensorOppsett(effektivPotte ?? { har_sensorer: false }));
  const effektivSensor = $derived(maskerAvlesning(effektiv?.sensor ?? sensor, sensOppsett) ?? null);
  const effektivePlanter = $derived(effektiv?.planter ?? planter);
  // Historikk-syntese er PotteDetalj-spesifikk (7-dagers vanntrend/sparkline
  // finnes ikke i oversikten) — samme sim-kilde (hentSim), bare lokal bruk.
  const sim = $derived(hentSim($simStore, potteId));
  const effektivHistorikk = $derived(
    (simAktiv && potte ? (simHistorikk(sim, potte) as SensorRad[]) : sensorHistorikk).map((r) =>
      maskerAvlesning(r, sensOppsett),
    ),
  );
  // Tankene (én i 1.0, venstre og høyre skuff i stor) med nivå og forbrukstrend.
  const vannTanker = $derived(
    effektivPotte
      ? kasseTanker(effektivPotte, sensOppsett).map((t) => tankStatus(t, effektivSensor, effektivHistorikk))
      : [],
  );
  // Kassas jordkalibrering (egne tall under tannhjulet, ellers de felles).
  const kal = $derived(jordGrenser(effektivPotte));

  // Samme regel som kortet på oversikten (utils.tilkoblingsStatus).
  const tilkobling = $derived(
    tilkoblingsStatus(!!effektivPotte?.har_sensorer, effektivSensor?.registrert_at, naa),
  );
  const detaljOffline = $derived(tilkobling === 'frakoblet');

  // To-fase næring: i ekte drift, eller i sim-forhåndsvisning.
  const naering = $derived(
    effektivPotte?.i_drift ? kasseNaering(effektivePlanter.map((p) => p.plantet_at)) : null,
  );

  // Rå ADC-serie per jordkanal fra historikken — delt av probe-, luft- og
  // overvåt-diagnosene så de garantert ser samme data.
  const kanalSerier = $derived.by(() =>
    [0, 1, 2, 3].map((idx) =>
      effektivHistorikk.map((r) => ({
        t: new Date(r.registrert_at ?? 0).getTime(),
        raw: [r.jord1, r.jord2, r.jord3, r.jord4][idx] ?? null,
      })),
    ),
  );

  // Maskinvare-diagnose fra 7-dagers historikken: løs/død probe, probe som
  // leser luft (ute av jorda), og veke-kontakt.
  const probeFunn = $derived.by(() => {
    if (!effektivPotte?.har_sensorer) return [] as { label: string; melding: string }[];
    const ut: { label: string; melding: string }[] = [];
    for (let kanal = 1; kanal <= 4; kanal++) {
      const f = probeHelse(kanalSerier[kanal - 1]!);
      if (f.melding && (f.status === 'frakoblet' || f.status === 'fastlast')) {
        ut.push({ label: sensorEtikett(kanal, effektivPotte.skillevegger, kasseForm(effektivPotte)), melding: f.melding });
      }
    }
    const grupper = vannTanker.length ? vannTanker.map((x) => x.tank.jordKanaler) : undefined;
    for (const f of luftFunn(kanalSerier, kal, grupper)) {
      ut.push({ label: sensorEtikett(f.kanal, effektivPotte.skillevegger, kasseForm(effektivPotte)), melding: f.melding });
    }
    return ut;
  });

  // Overvåt jord (per kanal): dagevis over VAAT_GRENSE = rotråte-risiko. Egen
  // seksjon (jordhelse, ikke maskinvarefeil) — nevner utsatte planter ved navn.
  const overvaatKanaler = $derived.by(() => {
    if (!effektivPotte?.har_sensorer) return [] as string[];
    const ut: string[] = [];
    for (let kanal = 1; kanal <= 4; kanal++) {
      const serie = kanalSerier[kanal - 1]!
        .map((p) => ({ t: p.t, pct: jordfuktProsent(p.raw, kal.torr, kal.vaat) ?? NaN }))
        .filter((p) => Number.isFinite(p.pct));
      if (overvaatHelse(serie).advar) {
        ut.push(sensorEtikett(kanal, effektivPotte.skillevegger, kasseForm(effektivPotte)));
      }
    }
    return ut;
  });
  const overvaatUtsatte = $derived(
    effektivePlanter
      .filter((p) => p.plante.veke_egnet === 'forsiktig' || p.plante.veke_egnet === 'ikke_anbefalt')
      .map((p) => p.plante.navn),
  );

  // Veka per tank: ble jorda tanken vanner, våtere etter påfyllingen? I stor vanner
  // hver skuff sin halvdel (to kopper), i 1.0 vanner tanken hele kassa.
  const vekeFunn = $derived.by(() => {
    const ut: { label: string; melding: string }[] = [];
    if (!effektivPotte?.har_sensorer) return ut;
    for (const { tank, trend } of vannTanker) {
      const jordSerie = effektivHistorikk.map((r) => {
        const raa = [r.jord1, r.jord2, r.jord3, r.jord4];
        const verdier = tank.jordKanaler
          .map((i) => jordfuktProsent(raa[i], kal.torr, kal.vaat))
          .filter((x): x is number => x !== null);
        return {
          t: new Date(r.registrert_at ?? 0).getTime(),
          pct: verdier.length ? verdier.reduce((a, b) => a + b, 0) / verdier.length : NaN,
        };
      });
      const f = vekeHelse(jordSerie, trend.sistFyltAt);
      if (f.advar && f.melding) {
        ut.push({ label: vannTanker.length > 1 ? `Veke, ${tank.navn.toLowerCase()}` : 'Veke', melding: f.melding });
      }
    }
    return ut;
  });

  const harDiagnose = $derived(probeFunn.length > 0 || vekeFunn.length > 0);

  // Når historikken sist ble hentet — refresh() laster den på nytt hvert kvarter
  // så vanntrend/sparkline/diagnose ikke fryses ved mount i lange økter.
  let sistHistorikkHentet = 0;
  const HISTORIKK_REFRESH_MS = 15 * 60_000;

  async function loadSensorHistorikk() {
    sistHistorikkHentet = Date.now();
    try {
      sensorHistorikk = await hentSensorHistorikk(potteId, 7);
    } catch (error) {
      // Behold siste komplette serie ved nettfeil, ikke en halv side med data.
      console.error('Sensorhistorikk feilet:', error);
    }
  }

  async function refresh() {
    naa = Date.now();
    if (henter) return; // tregt nett: ikke stable opp overlappende hentinger
    henter = true;
    try {
      const [cmd, sens] = await Promise.all([
        supabase
          .from('potte_commands')
          .select('*')
          .eq('potte_id', potteId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('potte_sensor_data')
          .select('*')
          .eq('potte_id', potteId)
          .order('registrert_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (!cmd.error) {
        command = cmd.data;
        lysKlar = true;
      }
      if (sens.data) sensor = sens.data;
      nettFeil = !!cmd.error || !!sens.error;
      if (Date.now() - sistHistorikkHentet > HISTORIKK_REFRESH_MS) {
        loadSensorHistorikk(); // bevisst uten await — trend kan oppdatere i bakgrunnen
      }
    } catch (e) {
      console.error('Detalj-refresh feilet:', e);
      nettFeil = true;
    } finally {
      henter = false;
      forsteHentet = true;
    }
  }

  // Intervallet startes med én gang (ikke etter en await — da ble det aldri
  // stoppet hvis man gikk tilbake før første henting var ferdig), og det
  // hviler mens appen ligger i bakgrunnen. Se lib/polling.ts.
  onMount(() => {
    refresh();
    loadPottePlanter(potteId);
    loadHistorikk();
    loadSensorHistorikk();
    return startPolling(refresh, 10_000);
  });

  async function loadHistorikk() {
    runderKlar = false;
    const rs = await supabase.from('potte_runder').select('*').eq('potte_id', potteId).order('startet_at', { ascending: false });
    runderFeil = !!rs.error;
    if (rs.data) runder = rs.data;
    runderKlar = true;
    const { data } = await supabase
      .from('potte_planter')
      .select('*, plante:planter(*)')
      .eq('potte_id', potteId)
      .not('fjernet_at', 'is', null)
      .order('fjernet_at', { ascending: false });
    if (data) historikk = data as unknown as PottePlanteFull[];
  }

  async function fjernPlante(pottePlanteId: string) {
    // I drift: myk-slett (behold for historikk). I testmodus: slett helt.
    const { data, error } = potte?.i_drift
      ? await supabase
          .from('potte_planter')
          .update({ fjernet_at: new Date().toISOString() })
          .eq('id', pottePlanteId).is('fjernet_at', null).select('id').single()
      : await supabase.from('potte_planter').delete().eq('id', pottePlanteId).is('fjernet_at', null).select('id').single();
    if (error || !data) {
      visFeil('Kunne ikke fjerne planten — prøv igjen.');
      return;
    }
    await Promise.all([loadPottePlanter(potteId), loadHistorikk()]);
  }

  /** Returnerer true når notatet er lagret — arket beholder teksten ellers. */
  async function lagreNotat(pottePlanteId: string, tekst: string): Promise<boolean> {
    const ok = await lagrePlanting(pottePlanteId, { notater: tekst || null });
    if (!ok) {
      visFeil('Kunne ikke lagre notatet — sjekk nettet og trykk «Lagre notat» igjen.');
      return false;
    }
    await loadPottePlanter(potteId);
    return true;
  }

  /** Ny plantedato for en aktiv planting (fra felt-arket). */
  async function lagreDato(pottePlanteId: string, iso: string): Promise<boolean> {
    const ok = await lagrePlanting(pottePlanteId, { plantet_at: iso });
    if (!ok) {
      visFeil('Kunne ikke lagre datoen — sjekk nettet og prøv igjen.');
      return false;
    }
    await loadPottePlanter(potteId);
    return true;
  }

  function formaterDato(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
  }
  function varighet(fra: string, til: string | null): string {
    const slutt = til ? new Date(til).getTime() : Date.now();
    const dager = Math.max(0, Math.floor((slutt - new Date(fra).getTime()) / 86400000));
    return `${dager} ${dager === 1 ? 'dag' : 'dager'}`;
  }

  function apneVelger(seksjon: number) {
    velgerSeksjon = seksjon;
  }

  /** Visningsnavn for en plass, f.eks. «Potte 1 · foran», «Potte 2» (udelt) eller «Kopp 3» (stor). */
  function plassLabelFor(seksjon: number): string {
    for (const po of oppsett) {
      const pl = po.plasser.find((x) => x.seksjon === seksjon);
      if (pl) {
        if (pl.rolle === 'kopp') return pl.etikett;
        return pl.rolle === 'hel'
          ? `Potte ${po.potteNr}`
          : `Potte ${po.potteNr} · ${pl.etikett.toLowerCase()}`;
      }
    }
    return `plass ${seksjon}`;
  }

  let bytterPlante = $state(false);
  async function planteValgt(planteId: string) {
    if (velgerSeksjon === null || !potte || bytterPlante) return;
    const seksjon = velgerSeksjon;
    const eksisterende = planter.find((pp) => pp.seksjon === seksjon);
    bytterPlante = true;
    try {
      const ok = await byttPlante(potteId, seksjon, planteId, eksisterende?.id ?? null, potte.i_drift);
      // Også ved tapt svar: databasen kan ha fullført før forbindelsen brøt.
      await Promise.allSettled([loadPottePlanter(potteId), loadHistorikk()]);
      if (ok) velgerSeksjon = null;
      else visFeil('Kunne ikke bekrefte plantebyttet. Kontroller kassa før du prøver igjen.');
    } finally {
      bytterPlante = false;
    }
  }

  /** Slå skillevegg av/på for én potte (beholder). */
  async function settSkillevegg(potteIdx: number, nyDelt: boolean) {
    const kasse = potte;
    if (!kasse) return;
    skilleveggFeil = null;
    // Slår man av skilleveggen, må den bakre plassen være tom — ellers ville
    // planten der blitt usynlig (seksjonen finnes fortsatt i databasen).
    if (!nyDelt && planter.some((p) => p.seksjon === bakSeksjon(potteIdx))) {
      skilleveggFeil = `Fjern planten i «bak» på Potte ${potteIdx + 1} før du tar bort skilleveggen.`;
      return;
    }
    const ny = [...kasse.skillevegger];
    ny[potteIdx] = nyDelt;
    const { data, error } = await supabase.from('potter').update({ skillevegger: ny }).eq('id', kasse.id).select('id').single();
    if (!error && data) {
      potter.update((liste) => liste.map((p) => (p.id === kasse.id ? { ...p, skillevegger: ny } : p)));
    } else {
      console.error('Lagring av skillevegg feilet:', error);
      skilleveggFeil = 'Kunne ikke lagre — prøv igjen (sjekk at du er innlogget).';
    }
  }
</script>

{#if !potte}
  <div class="text-text-muted text-sm">Blomsterkasse ikke funnet.</div>
{:else}
  <div class="max-w-[430px] mx-auto w-full flex flex-col gap-5">
    <!-- Header: tilbake + kassenavn + tilkoblet-dot -->
    <div class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-2.5 min-w-0">
        <button
          class="w-[34px] h-[34px] rounded-[10px] card flex items-center justify-center shrink-0 hover:brightness-125 transition-all"
          onclick={() => window.history.back()}
          aria-label="Tilbake"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <h1 class="font-display text-[22px] font-semibold leading-none truncate">{potte.navn}</h1>
      </div>
      <div class="flex items-center gap-3 shrink-0">
        {#if !potte.i_drift}
          <span class="font-mono text-[11px] text-sun" title="Testmodus. Byttes under tannhjulet.">🧪 Test</span>
        {/if}
        {#if tilkobling && (forsteHentet || simAktiv)}
          <span class="inline-flex items-center gap-1.5 font-mono text-[11px] {detaljOffline ? 'text-sun' : 'text-text-muted'}">
            {#if tilkobling === 'ingen-data'}
              <span class="w-[7px] h-[7px] rounded-full bg-text-dim"></span>
              Ingen data
            {:else}
              <span
                class="w-[7px] h-[7px] rounded-full {detaljOffline ? 'bg-sun' : 'bg-leaf'}"
                style="box-shadow:0 0 0 3px {detaljOffline ? 'rgba(251,191,36,0.16)' : 'rgba(74,222,128,0.16)'}"
              ></span>
              {detaljOffline ? 'Frakoblet' : 'Tilkoblet'}
            {/if}
          </span>
        {/if}
        <button
          class="w-[34px] h-[34px] rounded-[10px] card flex items-center justify-center hover:brightness-125 transition-all text-text-muted"
          onclick={() => (visInnstillinger = true)}
          aria-label="Innstillinger for kassa"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/></svg>
        </button>
      </div>
    </div>

    {#if nettFeil}
      <div
        class="flex items-center gap-2 px-3 py-2 rounded-[12px] bg-sun/[0.08] border border-sun/25 text-[12px] text-sun leading-snug"
        role="status"
      >
        <span aria-hidden="true">📡</span>
        Får ikke kontakt med databasen — tallene kan være gamle. Prøver igjen automatisk.
      </div>
    {/if}

    <!-- Anlegget: vekstlys + vannreservoar + pottene (oktagoner).
         I sim peker alt på syntetiske «effektiv»-data. -->
    <AnleggPanel
      potte={effektivPotte ?? potte}
      sensor={effektivSensor}
      {command}
      {lysKlar}
      tanker={vannTanker}
      {oppsett}
      planter={effektivePlanter.map((p) => p.plante)}
      pottePlanter={effektivePlanter}
      sensorHistorikk={effektivHistorikk}
      onAddPlante={apneVelger}
      onFjernPlante={fjernPlante}
      onToggleSkille={settSkillevegg}
      onLagreNotat={lagreNotat}
      onLagreDato={lagreDato}
      onCommandLagret={refresh}
      onVisGraf={(s) => (graf = { forvalgt: s })}
      simulert={simAktiv}
    />

    {#if effektivPotte?.har_sensorer}
      <button class="btn-secondary w-full" onclick={() => (graf = { forvalgt: null })}>
        📈 Grafer over sensorene
      </button>
    {/if}

    {#if skilleveggFeil}
      <div class="p-3 rounded-lg bg-rose/10 border border-rose/30 text-rose text-sm">
        {skilleveggFeil}
      </div>
    {/if}

    <!-- Overvåt jord: dagevis over VAAT_GRENSE — rotråte utvikler seg stille -->
    {#if overvaatKanaler.length > 0}
      <div class="card p-4 border-sky/30 bg-sky/[0.06]">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-base">🫧</span>
          <h2 class="font-display text-sm font-semibold">Svært våt jord over tid</h2>
        </div>
        <p class="text-xs text-text-muted leading-snug">
          <span class="text-text font-medium">{overvaatKanaler.join(', ')}</span> har stått svært
          vått i flere døgn. Sjekk at veka ikke overfôrer (færre/tynnere veker hjelper), og
          se/lukt etter råte i jorda.
          {#if overvaatUtsatte.length > 0}
            {overvaatUtsatte.join(' og ')} tåler konstant fukt dårlig — følg ekstra med.
          {/if}
        </p>
      </div>
    {/if}

    <!-- Maskinvare-sjekk: løs/død jordprobe, probe i lufta, eller veke uten kontakt -->
    {#if harDiagnose}
      <div class="card p-4 border-sun/30 bg-sun/[0.06]">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-base">🔧</span>
          <h2 class="font-display text-sm font-semibold">Maskinvare-sjekk</h2>
        </div>
        <ul class="space-y-1.5">
          {#each probeFunn as f (f.label + f.melding)}
            <li class="text-xs text-text-muted leading-snug">
              <span class="text-text font-medium">{f.label}:</span> {f.melding}
            </li>
          {/each}
          {#each vekeFunn as f (f.label)}
            <li class="text-xs text-text-muted leading-snug">
              <span class="text-text font-medium">{f.label}:</span> {f.melding}
            </li>
          {/each}
        </ul>
      </div>
    {/if}

    <!-- To-fase næring: minner om overgangen fra rent vann til næring -->
    {#if naering}
      <div class="card p-4 flex items-start gap-3 {naering.handlingNaa ? 'border-leaf/40' : ''}">
        <span class="text-xl shrink-0">{naering.handlingNaa ? '🧪' : '🌱'}</span>
        <div class="min-w-0">
          <div class="font-medium text-sm {naering.handlingNaa ? 'text-leaf-glow' : ''}">{naering.tittel}</div>
          <p class="text-text-muted text-xs mt-0.5 leading-snug">{naering.melding}</p>
        </div>
      </div>
    {/if}

    <!-- Veksttidslinje: kamerabilder fra Storage. Kun når kassa har kamera —
         ellers ville den vist en evig «ingen bilder ennå». -->
    {#if sensOppsett.kamera && runderKlar && !runderFeil && aktivRunde}
      {#key aktivRunde.id}
        <Veksttidslinje {potteId} fra={aktivRunde.startet_at} />
      {/key}
    {:else if sensOppsett.kamera && runderFeil}
      <p class="text-sm text-rose" role="alert">Kunne ikke hente dyrkingsrunden. Åpne kassa på nytt.</p>
    {/if}

    <button class="btn-secondary w-full" onclick={() => { valgtRunde = null; visHistorikk = true; }}>Tidligere runder</button>

    <!-- Drift/testmodus og simulatoren ligger under tannhjulet (KasseInnstillinger). -->
  </div>

  <Sheet open={graf !== null} onClose={() => (graf = null)} label="Grafer">
    {#if graf && potte}
      <SensorGrafer potte={effektivPotte ?? potte} pottePlanter={effektivePlanter} forvalgt={graf.forvalgt} />
    {/if}
  </Sheet>

  <Sheet open={visHistorikk} onClose={() => (visHistorikk = false)} label="Tidligere runder">
    <h2 class="font-display text-lg font-semibold">Tidligere runder</h2>
    {#if valgtRunde}
      <button class="btn-secondary my-3" onclick={() => (valgtRunde = null)}>Tilbake til rundene</button>
      <p class="text-sm mb-3">{formaterDato(valgtRunde.startet_at)}–{formaterDato(valgtRunde.avsluttet_at)}</p>
      {#key valgtRunde.id}
        <Veksttidslinje {potteId} fra={valgtRunde.startet_at} til={valgtRunde.avsluttet_at} />
      {/key}
    {:else}
      {#each tidligereRunder as r (r.id)}
        <button class="btn-secondary w-full mt-3 text-left" onclick={() => (valgtRunde = r)}>
          {formaterDato(r.startet_at)}–{formaterDato(r.avsluttet_at)} · planter og bilder
        </button>
      {/each}
      {#if tidligereRunder.length === 0 && vistHistorikk.length === 0}
        <p class="text-sm text-text-muted mt-3">Ingen tidligere runder ennå.</p>
      {/if}
    {/if}
    {#if vistHistorikk.length > 0}
      <section class="card p-5">
        <h3 class="font-display text-lg font-semibold">{valgtRunde ? 'Planter i runden' : 'Eldre plantinger'}</h3>
        <p class="text-text-muted text-xs mt-0.5 mb-3">Planter som har stått her før. Trykk for å rette eller slette.</p>
        <div class="-mx-2">
          {#each vistHistorikk as h (h.id)}
            <button
              class="w-full flex items-center gap-3 px-2 py-1.5 rounded-lg text-left hover:bg-surface-hover transition-colors"
              onclick={() => (redigerHist = h)}
            >
              <span class="text-xl shrink-0">{h.plante.emoji ?? '🌿'}</span>
              <span class="flex-1 min-w-0">
                <span class="block line-clamp-2 break-words text-sm">{h.plante.navn}</span>
                {#if h.notater}
                  <span class="block truncate text-[11px] text-text-dim italic">{h.notater}</span>
                {/if}
              </span>
              <span class="text-xs text-text-dim shrink-0 text-right leading-tight">
                stod {varighet(h.plantet_at, h.fjernet_at)}<br />
                <span class="text-[11px]">{formaterDato(h.plantet_at)}–{formaterDato(h.fjernet_at)}</span>
              </span>
            </button>
          {/each}
        </div>
      </section>
    {/if}

  </Sheet>

  {#if velgerSeksjon !== null}
    <PlanteVelger
      plassLabel={plassLabelFor(velgerSeksjon)}
      eksisterendePlanter={planter.map((p) => p.plante)}
      onLukk={() => (velgerSeksjon = null)}
      onValgt={planteValgt}
    />
  {/if}

  <Sheet open={visInnstillinger} onClose={() => (visInnstillinger = false)} label="Innstillinger for kassa">
    {#if visInnstillinger && potte}
      <KasseInnstillinger
        kasse={potte}
        sisteAvlesning={maskerAvlesning(sensor, sensorOppsett(potte))}
        onLukk={() => (visInnstillinger = false)}
        onLagret={() => {
          // Slettet kasse → gå tilbake til oversikten.
          if (!$potter.find((p) => p.potte_id === potteId)) window.history.back();
          else loadHistorikk(); // «Ny runde» legger den gamle runden i historikken
        }}
      />
    {/if}
  </Sheet>

  <Sheet open={redigerHist !== null} onClose={() => (redigerHist = null)} label="Rediger historikk">
    {#if redigerHist}
      {#key redigerHist.id}
        <PlantingRedigering planting={redigerHist} onLukk={() => (redigerHist = null)} onEndret={loadHistorikk} />
      {/key}
    {/if}
  </Sheet>
{/if}
